/**
 * Name Validation and Normalization Utility (Frontend)
 * 
 * Mirrors backend validation logic to provide real-time feedback
 * while the user is creating folders and test suites.
 */

/**
 * Validate and normalize a name according to rules
 * @param {string} name - The name to validate
 * @param {Object} options - Validation options
 * @param {number} options.maxLength - Maximum allowed length
 * @param {boolean} options.normalize - Whether to return normalized name
 * @returns {Object} - { isValid: boolean, errors: string[], normalized: string, warnings: string[] }
 */
export function validateName(name, options = {}) {
  const { maxLength = 50, normalize = false } = options;
  const errors = [];
  const warnings = [];

  if (!name || typeof name !== 'string') {
    return { 
      isValid: false, 
      errors: ['Name is required'], 
      normalized: '', 
      warnings: [] 
    };
  }

  let normalized = name.trim();

  if (normalized.length === 0) {
    return { 
      isValid: false, 
      errors: ['Name cannot be empty'], 
      normalized: '', 
      warnings: [] 
    };
  }

  // Check for unsafe characters (< > \ ^ ` ~ | * ? ! { })
  const hasUnsafeChars = /[<>\\^`~|*?!{}]/.test(normalized);
  if (hasUnsafeChars) {
    if (normalize) {
      normalized = normalized.replace(/[<>\\^`~|*?!{}]/g, '');
      warnings.push('Some unsafe characters were removed');
    } else {
      errors.push('Name contains invalid characters (< > \\ ^ ` ~ | * ? ! { } are not allowed)');
    }
  }

  if (normalize) {
    // Collapse multiple spaces
    normalized = normalized.replace(/\s+/g, ' ').trim();
  }

  // Must contain at least 1 letter
  const alphabetCount = (normalized.match(/[a-zA-Z]/g) || []).length;
  if (alphabetCount < 1) {
    errors.push(`Name must contain at least one letter`);
  }

  // Check length
  if (normalized.length > maxLength) {
    if (normalize) {
      warnings.push(`Name will be trimmed from ${normalized.length} to ${maxLength} characters`);
      normalized = normalized.substring(0, maxLength);
      // Re-trim trailing spaces and underscores after cutting
      normalized = normalized.replace(/[\s_]+$/, '');
    } else {
      errors.push(`Name exceeds maximum length of ${maxLength} characters (current: ${normalized.length})`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    normalized,
    warnings
  };
}

/**
 * Validate folder name
 * @param {string} name - Folder name to validate
 * @param {Object} options - Validation options
 * @returns {Object} - Validation result
 */
export function validateFolderName(name, options = {}) {
  return validateName(name, { maxLength: options.maxLength || 200, normalize: options.normalize });
}

/**
 * Validate test suite name
 * @param {string} name - Test suite name to validate
 * @param {Object} options - Validation options
 * @returns {Object} - Validation result
 */
export function validateTestSuiteName(name, options = {}) {
  return validateName(name, { maxLength: options.maxLength || 200, normalize: options.normalize });
}

/**
 * Get character count display with color coding
 * @param {number} length - Current length
 * @param {number} maxLength - Maximum length
 * @returns {Object} - { text: string, className: string }
 */
export function getCharacterCountDisplay(length, maxLength) {
  const remaining = maxLength - length;
  let className = 'text-muted';
  
  if (remaining <= 0) {
    className = 'text-danger';
  } else if (remaining <= 5) {
    className = 'text-warning';
  }
  
  return {
    text: `${length}/${maxLength} characters`,
    className
  };
}
